import {
  userCompleteResetPasswordSuccess,
  userLoginSuccess,
} from '@actions/user.action';
import { Action, createReducer, on } from '@ngrx/store';
import { ApiError, CloudflareMeta, Error, ErrorType } from '@models/app.models';
import {
  appAbandonTokenSuccess,
  appClearFormError,
  appCreateTokenSuccess,
  appHideLoader,
  appNorseApiRequest,
  appReset,
  appResetSteps,
  appShowLoader,
  appSetShowModifySearchWidget,
  onError,
  onFormError,
  appSetAutoSearch,
  appApiError,
  appApiClearErrors,
  appNorseApiRequestCompleted,
  appSetActiveStep,
  appGetTokenInfoSuccess,
  appGetTokenInfoFailure,
  AppActionTypes,
  appStoreWindowSize,
  appStart,
  appUpdateModifySearchWidget,
  appShowTripSummary,
  appHideTripSummary,
  appToggleTripSummary,
  autoRefreshToken,
  updateCloudflareMeta, loadDynamicPagesConfigSuccess,
} from '../actions/app.action';
import { FlowPathEnum, PathStep } from '@models/flow-wrapper.model';
import {
  availabilityInvalidSearchByQueryParams,
  availabilitySearchAll,
  availabilitySearchAllHome,
  availabilitySearchAllModify,
  availabilitySearchByQueryParams,
  availabilitySearchFromDestinations,
  availabilitySearchFareAvailabilitySuccess,
  availabilitySearchFares,
  availabilitySellTripSelectionByQueryParams,
  availabilitySellTripSelectionSuccess,
} from '@actions/availability.action';
import {
  updatePassengers,
  updatePassengersSuccess,
  updateTripsPassengersSuccess,
} from '@actions/passenger.action';
import {
  BookingActionTypes,
  bookingAddBags,
  bookingAddExtras,
  bookingCancelFlightsSuccess,
  bookingCancelWithPaymentPaymentSuccess,
  bookingCancelZeroBalance,
  bookingCheckInFlights,
  bookingFurtherPaymentRequired,
  bookingManageBooking,
  bookingPaymentsAcceptedBookingUpdated,
  bookingPaymentsDeclined,
  bookingRetrieveBooking,
  bookingRetrieveBookingDeeplinkBags,
  bookingRetrieveBookingDeeplinkExtras,
  bookingRetrieveBookingDeeplinkMeals,
  bookingRetrieveBookingDeeplinkPay,
  bookingRetrieveBookingDeeplinkSeats,
  bookingTravelerChange,
  bookingUpgradeCabin,
  bookingViewChangeMeals,
  bookingViewChangeSeats, upgradeToEconomyClassicActionSuccess,
} from '@actions/booking.action';
import {
  bagsSellBagSsrsCheckInSuccess,
  bagsSellBagSsrsSuccess,
  bagsValidateBagSelection,
} from '@actions/bags.action';
import { updatePassengerSeatsSuccess } from '@actions/seat.action';
import {
  AGENT_AUTHENTICATION_ERROR_CODE,
  BREAKPOINT_LARGE_DESKTOP,
  DEFAULT_TOKEN_TIMEOUT_MINUTES,
  DeviceHeightClassification,
  DEVICE_HEIGHT_SMALL,
  TOKEN_TIMEOUT_ERROR_CODE,
  WindowSizeClassification,
  BREAKPOINT_TABLET,
  BREAKPOINT_MOBILE,
  BREAKPOINT_MOBILE_SMALL,
  BREAKPOINT_MEDIUM_DESKTOP,
} from '@constants/app.constants';
import { extrasSellExtrasSsrs, extrasSellExtrasSsrsSuccess } from '@actions/extras.action';
import { mealsSellMealsSsrSuccess, mealsSellSelectedMeals } from '@actions/meals.action';
import {
  clearPaymentErrors, paymentRefundPaymentsSuccess,
  paymentSubmitPayments
} from '@actions/payment.action';
import { tripDetailsPayBalance } from '@actions/trip-details.action';
import { DateTime } from 'luxon';
import { AutoSearch } from '@models/auto-search.model';
import {
  routerCancelAction,
  routerErrorAction,
  routerNavigatedAction,
  routerRequestAction,
} from '@ngrx/router-store';
import { flightValidateSelections } from '@actions/flight.action';
import { v4 as uuidv4 } from 'uuid';
import { cmsDisruptionBannerRemove, cmsDisruptionBannersClear, cmsDisruptionBannersSuccess } from '@actions/cms.action';
import { DisruptionBanner } from '@models/cms/disruption-banner.model';
import { checkInDocCheckSuccess } from '@actions/check-in.action';
import { DynamicPageConfig } from "@models/cms/dynamic-pages-umbraco/dynamic-pages-config.model";

export interface State {
  apiErrors: ApiError[];
  appId: string;
  autoSearch: AutoSearch;
  autoSell: boolean;
  disruptionBanners: DisruptionBanner[];
  disruptionBannersClosed: string[];
  cloudflareMeta: CloudflareMeta | null;
  deviceHeightClassification: DeviceHeightClassification;
  doShowLoader: boolean;
  dynamicPagesConfig: DynamicPageConfig[] | null;
  isDynamicPagesConfigLoaded: boolean;
  errors: Error[];
  formError: Error | null;
  isTokenProvenValid: boolean;
  ongoingRequestCount: number;
  pathSteps: PathStep[];
  platforms: string[];
  shouldDeferUiUpdates: boolean;
  showModifySearchNavbarWidget: boolean;
  showModifySearchWidget: boolean;
  showTripSummary: boolean;
  skipToPaymentModalDisplayed?: boolean;
  tokenAutoRefreshCount: number;
  tokenRefreshedAt: Date | null;
  tokenTimeoutInMinutes: number | null;
  windowSizeClassification: WindowSizeClassification;
}

export const INITIAL_STATE: State = {
  errors: [],
  apiErrors: [],
  formError: null,
  // timeout in minutes returned from token create call
  tokenTimeoutInMinutes: DEFAULT_TOKEN_TIMEOUT_MINUTES,
  // last the time the token was refreshed (an API call was made)
  tokenRefreshedAt: null,
  isTokenProvenValid: false,
  pathSteps: [],
  ongoingRequestCount: 0,
  doShowLoader: false,
  dynamicPagesConfig: null,
  isDynamicPagesConfigLoaded: false,
  showModifySearchNavbarWidget: true,
  showModifySearchWidget: false,
  autoSearch: {
    search: false,
    clearBooking: false,
  },
  autoSell: false,
  shouldDeferUiUpdates: false,
  windowSizeClassification: WindowSizeClassification.Desktop,
  platforms: [],
  showTripSummary: false,
  tokenAutoRefreshCount: 0,
  deviceHeightClassification: DeviceHeightClassification.Other,
  appId: uuidv4(),
  disruptionBanners: [],
  disruptionBannersClosed: [],
  skipToPaymentModalDisplayed: false,
  cloudflareMeta: null,
};

const reducer = createReducer(
  INITIAL_STATE,
  on(
    // eslint-disable-next-line ngrx/avoid-duplicate-actions-in-reducer
    appStart,
    (state, action): State => ({
      ...state,
      platforms: action.platforms,
    }),
  ),

  on(
    routerRequestAction,
    paymentSubmitPayments,
    (state): State => ({
      ...state,
      shouldDeferUiUpdates: true,
    }),
  ),

  on(
    routerNavigatedAction,
    routerCancelAction,
    routerErrorAction,
    bookingFurtherPaymentRequired,
    bookingPaymentsDeclined,
    onError,
    (state, action): State => ({
      ...state,
      // always stop deffering UI updates, except on non-payment errors.
      shouldDeferUiUpdates:
        action.type === AppActionTypes.error &&
        !(
          action.error.type === ErrorType.SubmitPaymentFailure ||
          action.error.type === ErrorType.BookingPaymentsDeclined
        ),
    }),
  ),

  on(
    appGetTokenInfoSuccess,
    (state): State => ({
      ...state,
      isTokenProvenValid: true,
    }),
  ),

  on(
    appGetTokenInfoFailure,
    (state): State => ({
      ...state,
      isTokenProvenValid: false,
    }),
  ),

  on(appCreateTokenSuccess, (state, action): State => {
    return {
      ...state,
      tokenTimeoutInMinutes: action.idleTimeoutInMinutes,
      tokenRefreshedAt: new Date(),
      tokenAutoRefreshCount: 0
    };
  }),

  on(
    appSetAutoSearch,
    (state, action): State => ({
      ...state,
      autoSearch: {
        ...state.autoSearch,
        search: action.payload.search,
        clearBooking: action.payload.clearBooking,
      },
      showModifySearchWidget: action.payload.search,
    }),
  ),

  on(
    availabilitySearchByQueryParams,
    (state): State => ({
      ...state,
      autoSearch: {
        search: true,
        clearBooking: true,
      },
    }),
  ),

  on(
    availabilityInvalidSearchByQueryParams,
    (state): State => ({
      ...state,
      autoSearch: {
        search: false,
        clearBooking: false,
      },
    }),
  ),

  on(
    availabilitySellTripSelectionByQueryParams,
    (state): State => ({
      ...state,
      autoSell: true,
    }),
  ),

  on(
    availabilitySellTripSelectionSuccess,
    availabilitySearchAll,
    availabilitySearchAllHome,
    availabilitySearchAllModify,
    availabilitySearchFares,
    availabilitySearchFromDestinations,
    (state): State => ({
      ...state,
      autoSell: false,
    }),
  ),

  on(
    appShowLoader,
    (state): State => ({
      ...state,
      doShowLoader: true,
    }),
  ),

  on(
    appHideLoader,
    (state): State => ({
      ...state,
      doShowLoader: false,
    }),
  ),
  //TODO PENDING REQUESTS REFACTOR
  // on every api request, reset the token refresh time
  // except for CMS requests
  on(appNorseApiRequest, (state, action): State => {
    const isCmsRequest = action.req.url.includes('/cms');
    const isAutoKeepalive =
      action.req.url.includes('/keepalive') &&
      !action.req.url.includes('fromTimeoutModal');
    const excludedPaymentRequests = action.req.url.includes('transactions/status')
      || (action.req.url.includes('payments/encrypted') && action.req.method === 'POST');

    return {
      ...state,
      tokenRefreshedAt: isCmsRequest ? state.tokenRefreshedAt : new Date(),
      ongoingRequestCount: isAutoKeepalive || excludedPaymentRequests
        ? state.ongoingRequestCount
        : state.ongoingRequestCount + 1,
    };
  }),
  //TODO PENDING REQUESTS REFACTOR
  on(appNorseApiRequestCompleted, (state, action): State => {
    const isAutoKeepalive =
      action.req.url.includes('/keepalive') &&
      !action.req.url.includes('fromTimeoutModal');
    const excludedPaymentRequests = action.req.url.includes('transactions/status')
      || (action.req.url.includes('payments/encrypted') && action.req.method === 'POST');

    return {
      ...state,
      ongoingRequestCount: isAutoKeepalive || excludedPaymentRequests
        ? state.ongoingRequestCount
        : state.ongoingRequestCount - 1,
    };
  }),
  //TODO PENDING REQUESTS REFACTOR
  on(appNorseApiRequest, appNorseApiRequestCompleted, (state): State => {
    return {
      ...state,
      doShowLoader: state.ongoingRequestCount >= 1,
    };
  }),

  on(
    bookingPaymentsDeclined,
    onError,
    (state, action): State => ({
      ...state,
      errors: [action.error],
    }),
  ),

  on(
    paymentSubmitPayments,
    clearPaymentErrors,
    (state): State => ({
      ...state,
      errors: state.errors.filter(
        (err) =>
          err.type !== ErrorType.SubmitPaymentFailure &&
          err.type !== ErrorType.BookingPaymentsDeclined,
      ),
    }),
  ),

  on(appApiError, (state, action): State => {
    const isTokenTimeout = action.errors.some(
      (err) =>
        err.code === TOKEN_TIMEOUT_ERROR_CODE ||
        err.code === AGENT_AUTHENTICATION_ERROR_CODE,
    );
    // since we set the refresh date on request, we need to set it to a value that would cause the modal
    // to show that the token is timed out whenever we receive a timeout error.
    const overrideRefreshDateForTokenTimeout = DateTime.now()
      .minus({ minutes: state.tokenTimeoutInMinutes ?? 0 })
      .toJSDate();
    return {
      ...state,
      apiErrors: getUniqueErrorsByCode([...state.apiErrors, ...action.errors]),
      tokenRefreshedAt: isTokenTimeout
        ? overrideRefreshDateForTokenTimeout
        : state.tokenRefreshedAt,
    };
  }),

  on(
    appApiClearErrors,
    (state): State => ({
      ...state,
      apiErrors: [],
    }),
  ),

  on(
    onFormError,
    (state, action): State => ({
      ...state,
      formError: action.error,
    }),
  ),

  on(
    appClearFormError,
    userLoginSuccess,
    userCompleteResetPasswordSuccess,
    (state): State => ({
      ...state,
      formError: null,
    }),
  ),

  // #region BOOK STATE
  on(
    appSetActiveStep,
    (state, action): State => ({
      ...state,
      pathSteps: state.pathSteps.map((s) => {
        if (action.stepName === FlowPathEnum.CONFIRMATION) {
          return s.name === action.stepName
            ? { ...s, active: true, disabled: true }
            : { ...s, active: false, disabled: true };
        }

        return s.name === action.stepName
          ? { ...s, active: true, disabled: false }
          : { ...s, active: false };
      }),
    }),
  ),

  on(
    availabilitySellTripSelectionSuccess,
    (state): State => ({
      ...state,
      pathSteps: state.pathSteps.map((s) => {
        return s.name === FlowPathEnum.FLIGHTS ? { ...s, completed: true } : s;
      }),
    }),
  ),

  on(
    upgradeToEconomyClassicActionSuccess,
    (state, action): State => {
      let pathSteps = state.pathSteps;
      if (action.redirect) {
        pathSteps = pathSteps
          .map((s) => s.name === FlowPathEnum.BAGS && action.redirect ? {...s, completed: true, active: false} : s)
          .map((s) => s.name === FlowPathEnum.SEATS && action.redirect ? {...s, disabled: false, active: true} : s)
      }
      return {
        ...state,
        pathSteps
      }
    }
  ),

  on(
    availabilitySearchFareAvailabilitySuccess, (state): State => ({
      ...state,
      pathSteps: state.pathSteps.map((s) => {
        return s.name === FlowPathEnum.FLIGHTS ? { ...s, completed: false } : s;
      }),
    }),
  ),

  on(
    bagsSellBagSsrsCheckInSuccess,
    (state): State => ({
      ...state,
      pathSteps: state.pathSteps.map((s) => {
        return s.name === FlowPathEnum.BAGS ? { ...s, completed: true } : s;
      }),
    }),
  ),

  on(
    bagsSellBagSsrsSuccess,
    (state, action): State => {
      // redirectToPayment is needed for feat-002191-skip-to-payment-button only
      const pathSteps = action.redirectToPayment
        ? setSkipToPaymentPath(state)
        : state.pathSteps.map((s) => {
          return s.name === FlowPathEnum.BAGS
            ? {...s, completed: true}
            : s;
        })
      return {
        ...state,
        pathSteps,
      }
    }
  ),

  on(
    updatePassengerSeatsSuccess,
    (state, action): State => {
      // redirectToPayment is needed for feat-002191-skip-to-payment-button only
      const pathSteps = action.redirectToPayment
        ? setSkipToPaymentPath(state)
        : state.pathSteps.map((s) => {
          return s.name === FlowPathEnum.SEATS
            ? {...s, completed: true}
            : s;
        })
      return {
        ...state,
        pathSteps,
      }
    }
  ),

  on(
    mealsSellMealsSsrSuccess,
    (state, action): State => {
      // redirectToPayment is needed for feat-002191-skip-to-payment-button only
      const pathSteps = action.redirectToPayment
        ? setSkipToPaymentPath(state)
        : state.pathSteps.map((s) => {
          return s.name === FlowPathEnum.MEALS
            ? {...s, completed: true}
            : s;
        })
      return {
        ...state,
        pathSteps,
      }
    }
  ),

  on(
    extrasSellExtrasSsrsSuccess,
    (state, action): State => {
      // redirectToPayment is needed for feat-002191-skip-to-payment-button only
      const pathSteps = action.redirectToPayment
        ? setSkipToPaymentPath(state)
        : state.pathSteps.map((s) => {
          return s.name === FlowPathEnum.EXTRAS
            ? {...s, completed: true}
            : s;
        })
      return {
        ...state,
        pathSteps,
      }
    }
  ),

  on(
    updatePassengersSuccess,
    (state, action): State => {
      // redirectToPayment is needed for feat-002191-skip-to-payment-button only
      const pathSteps = action.redirectToPayment
        ? setSkipToPaymentPath(state)
        : state.pathSteps.map((s) => {
        return s.name === FlowPathEnum.TRAVELERS
          ? {...s, completed: true}
          : s;
      })
      return {
        ...state,
        pathSteps,
      }
    }
  ),

  on(
    updateTripsPassengersSuccess,
    (state): State => ({
      ...state,
      pathSteps: state.pathSteps.map((s) => {
        return s.name === FlowPathEnum.DOCUMENTS
          ? { ...s, completed: true }
          : s;
      }),
    }),
  ),

  on(
    checkInDocCheckSuccess,
    (state): State => ({
      ...state,
      pathSteps: state.pathSteps.map((s) => {
        return s.name === FlowPathEnum.DOCUMENTS
          ? { ...s, completed: true }
          : s;
      })
    })
  ),

  on(
    bookingPaymentsAcceptedBookingUpdated,
    bookingCancelZeroBalance,
    paymentRefundPaymentsSuccess,
    bookingCancelWithPaymentPaymentSuccess,
    bookingPaymentsDeclined,
    onError,
    (state, action): State => ({
      ...state,
      pathSteps: state.pathSteps.map((s) => {
        return s.name === FlowPathEnum.PAYMENT || s.name === FlowPathEnum.REVIEW
          ? // payment/review is completed iff a refund/cancellation or the balance due
            // is <= 0 on a completed payment
            {
              ...s,
              completed:
                (action.type === AppActionTypes.error &&
                  action.error.type === ErrorType.BookingRefundFailure) ||
                action.type !==
                  BookingActionTypes.bookingPaymentsAcceptedBookingUpdated ||
                (action.booking.breakdown?.balanceDue ?? 0) <= 0,
            }
          : s;
      }),
    }),
  ),
  // #endregion

  on(
    appResetSteps,
    appReset,
    appAbandonTokenSuccess,
    (state): State => ({
      ...state,
      pathSteps: [],
      tokenAutoRefreshCount: 0
    }),
  ),

  // Actions that start book flow
  on(
    availabilitySearchAllHome,
    availabilitySearchByQueryParams,
    availabilitySearchFromDestinations,
    availabilitySellTripSelectionByQueryParams,
    (state, action): State => {
      const defaultPathStep: Omit<PathStep, 'name' | 'icon'> = {
        completed: false,
        active: false,
        disabled: true,
      };
      const flightsPathStep: PathStep = {
        ...defaultPathStep,
        name: FlowPathEnum.FLIGHTS,
        icon: 'booking-flights',
        active: true,
        disabled: false,
      };
      const bagsPathStep: PathStep = {
        ...defaultPathStep,
        name: FlowPathEnum.BAGS,
        icon: 'booking-bags',
      };
      const seatsPathStep: PathStep = {
        ...defaultPathStep,
        name: FlowPathEnum.SEATS,
        icon: 'booking-seats',
      };
      const mealsPathStep: PathStep = {
        ...defaultPathStep,
        name: FlowPathEnum.MEALS,
        icon: 'booking-meals',
      };
      const extrasPathStep: PathStep = {
        ...defaultPathStep,
        name: FlowPathEnum.EXTRAS,
        icon: 'booking-extras',
      };
      const travelersPathStep: PathStep = {
        ...defaultPathStep,
        name: FlowPathEnum.TRAVELERS,
        icon: 'booking-travelers',
      };
      const paymentPathStep: PathStep = {
        ...defaultPathStep,
        name: FlowPathEnum.PAYMENT,
        icon: 'booking-payment',
      };
      const confirmationPathStep: PathStep = {
        ...defaultPathStep,
        name: FlowPathEnum.CONFIRMATION,
        icon: 'booking-confirmation',
      };

      const pathSteps = [
        flightsPathStep,
        bagsPathStep,
        seatsPathStep,
        mealsPathStep,
        extrasPathStep,
        travelersPathStep,
        paymentPathStep,
        confirmationPathStep,
      ];

      if (action.isSeatsBeforeBags) {
        const seatsIndex = pathSteps.findIndex(step => step.name === FlowPathEnum.SEATS);
        // remove seats from current position
        pathSteps.splice(seatsIndex, 1);
        // add seats to second position after flights
        pathSteps.splice(1, 0, seatsPathStep);
      }

      if (action.isTravelersStart) {
        const travelersIndex = pathSteps.findIndex(step => step.name === FlowPathEnum.TRAVELERS);
        // remove travelers from current position
        pathSteps.splice(travelersIndex, 1);
        // add travelers to second position after flights
        pathSteps.splice(1, 0, travelersPathStep);
      }

      return {
        ...state,
        pathSteps,
      }
    },
  ),

  // Actions that start pay remaining balance flow
  on(
    tripDetailsPayBalance,
    bookingRetrieveBookingDeeplinkPay,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start change flight flow
  on(
    availabilitySearchAllModify,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.FLIGHTS,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-flights',
        },
        {
          name: FlowPathEnum.BAGS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-bags',
        },
        {
          name: FlowPathEnum.SEATS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-seats',
        },
        {
          name: FlowPathEnum.MEALS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-meals',
        },
        {
          name: FlowPathEnum.EXTRAS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-extras',
        },
        {
          name: FlowPathEnum.TRAVELERS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-travelers',
        },
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start manage flow
  on(
    bookingManageBooking,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.BAGS,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-bags',
        },
        {
          name: FlowPathEnum.SEATS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-seats',
        },
        {
          name: FlowPathEnum.MEALS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-meals',
        },
        {
          name: FlowPathEnum.EXTRAS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-extras',
        },
        {
          name: FlowPathEnum.TRAVELERS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-travelers',
        },
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start add bags flow
  on(
    bookingAddBags,
    bookingRetrieveBookingDeeplinkBags,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.BAGS,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-bags',
        },
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start view/change seats flow
  on(
    bookingViewChangeSeats,
    bookingRetrieveBookingDeeplinkSeats,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.SEATS,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-seats',
        },
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start view/change meals flow
  on(
    bookingViewChangeMeals,
    bookingRetrieveBookingDeeplinkMeals,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.MEALS,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-meals',
        },
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start add extras flow
  on(
    bookingAddExtras,
    bookingRetrieveBookingDeeplinkExtras,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.EXTRAS,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-extras',
        },
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start check in flow
  on(
    bookingCheckInFlights,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.DOCUMENTS,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-documents',
        },
        {
          name: FlowPathEnum.BAGS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-bags',
        },
        {
          name: FlowPathEnum.SEATS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-seats',
        },
        {
          name: FlowPathEnum.MEALS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-meals',
        },
        {
          name: FlowPathEnum.EXTRAS,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-extras',
        },
        {
          name: FlowPathEnum.REVIEW,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-review',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start traveler change flow
  on(
    bookingTravelerChange,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.TRAVELERS,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-travelers',
        },
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  // Actions that start upgrade flow
  on(
    bookingUpgradeCabin,
    (state, action): State => ({
      ...state,
      pathSteps: action.compressedFlow ? [
          {
            name: FlowPathEnum.FLIGHTS,
            completed: false,
            active: true,
            disabled: false,
            icon: 'booking-flights',
          },
          {
            name: FlowPathEnum.PAYMENT,
            completed: false,
            active: false,
            disabled: true,
            icon: 'booking-payment',
          },
          {
            name: FlowPathEnum.CONFIRMATION,
            completed: false,
            active: false,
            disabled: true,
            icon: 'booking-confirmation',
          },
        ] :
        [
          {
            name: FlowPathEnum.FLIGHTS,
            completed: false,
            active: true,
            disabled: false,
            icon: 'booking-flights',
          },
          {
            name: FlowPathEnum.SEATS,
            completed: false,
            active: false,
            disabled: true,
            icon: 'booking-seats',
          },
          {
            name: FlowPathEnum.PAYMENT,
            completed: false,
            active: false,
            disabled: true,
            icon: 'booking-payment',
          },
          {
            name: FlowPathEnum.CONFIRMATION,
            completed: false,
            active: false,
            disabled: true,
            icon: 'booking-confirmation',
          },
      ],
    }),
  ),

  // Start cancel flow
  on(
    bookingCancelFlightsSuccess,
    (state): State => ({
      ...state,
      pathSteps: [
        {
          name: FlowPathEnum.PAYMENT,
          completed: false,
          active: true,
          disabled: false,
          icon: 'booking-payment',
        },
        {
          name: FlowPathEnum.CONFIRMATION,
          completed: false,
          active: false,
          disabled: true,
          icon: 'booking-confirmation',
        },
      ],
    }),
  ),

  on(
    appSetShowModifySearchWidget,
    (state, action): State => ({
      ...state,
      showModifySearchWidget: action.show,
      showTripSummary: false,
    }),
  ),

  on(
    appUpdateModifySearchWidget,
    (state, action): State => ({
      ...state,
      showModifySearchWidget: action.showWidget,
      showModifySearchNavbarWidget: action.showLink,
      showTripSummary: false,
    }),
  ),

  on(
    availabilitySearchAll,
    availabilitySearchAllHome,
    (state): State => ({
      ...state,
      showTripSummary: false,
      showModifySearchWidget: false,
      showModifySearchNavbarWidget: true,
      autoSearch: {
        search: false,
        clearBooking: false,
      },
    }),
  ),

  on(
    // eslint-disable-next-line ngrx/avoid-duplicate-actions-in-reducer
    appStart,
    appStoreWindowSize,
    appReset,
    (state, action): State => {
      const mobileWidth = action.isMobileFromTablet
        ? action.width < BREAKPOINT_TABLET
        : action.width < BREAKPOINT_MOBILE;

      const deviceHeight = action.height <= DEVICE_HEIGHT_SMALL
        ? DeviceHeightClassification.Small
        : DeviceHeightClassification.Other;

      if (action.width >= BREAKPOINT_LARGE_DESKTOP) {
        return {
          ...state,
          deviceHeightClassification: deviceHeight,
          windowSizeClassification: WindowSizeClassification.LargeDesktop,
        };
      }
      if (action.width <= BREAKPOINT_MOBILE_SMALL) {
        return {
          ...state,
          deviceHeightClassification: deviceHeight,
          windowSizeClassification: WindowSizeClassification.SmallMobile,
        };
      }

      if (action.width >= BREAKPOINT_MEDIUM_DESKTOP) {
        return {
          ...state,
          deviceHeightClassification: deviceHeight,
          windowSizeClassification: WindowSizeClassification.MediumDesktop,
        };
      }

      if (action.width >= BREAKPOINT_MOBILE && action.width < BREAKPOINT_TABLET) {
        return {
          ...state,
          deviceHeightClassification: deviceHeight,
          windowSizeClassification: WindowSizeClassification.Tablet,
        };
      }

      return {
      ...state,
        deviceHeightClassification: deviceHeight,
        windowSizeClassification:
          mobileWidth
            ? WindowSizeClassification.Mobile
            : WindowSizeClassification.Desktop,
      }
    },
  ),

  on(
    appShowTripSummary,
    (state): State => ({
      ...state,
      showTripSummary: true,
    }),
  ),

  on(
    appHideTripSummary,
    bagsValidateBagSelection,
    flightValidateSelections,
    updatePassengers,
    mealsSellSelectedMeals,
    extrasSellExtrasSsrs,
    bookingRetrieveBooking,
    (state): State => ({
      ...state,
      showTripSummary: false,
    }),
  ),

  on(
    appToggleTripSummary,
    (state): State => ({
      ...state,
      showTripSummary: !state.showTripSummary,
    }),
  ),

  on(
    flightValidateSelections,
    (state): State => ({
      ...state,
      skipToPaymentModalDisplayed: false
    }),
  ),

  on(autoRefreshToken,
    (state): State => ({
      ...state,
      tokenAutoRefreshCount: state.tokenAutoRefreshCount + 1
    })
  ),

  // DISRUPTION BANNERS
  on(
    cmsDisruptionBannersSuccess,
    (state, action): State => ({
      ...state,
      disruptionBanners: action.banners
    }),
  ),

  on(
    cmsDisruptionBannersClear,
    (state): State => ({
      ...state,
      disruptionBanners: [],
      disruptionBannersClosed: [],
    }),
  ),

  on(
    cmsDisruptionBannerRemove,
    (state, action): State => ({
      ...state,
      disruptionBannersClosed: [...state.disruptionBannersClosed, action.id],
    }),
  ),

  on(
    updatePassengers,
    (state, action): State => {
      // needed for feat-002191-skip-to-payment-button only
      return {
        ...state,
        skipToPaymentModalDisplayed: action.skipToPaymentModalDisplayed
      }
    }
  ),

  on(
    updateCloudflareMeta,
    (state, action): State => ({
      ...state,
      cloudflareMeta: action.meta,
    })
  ),

  on(
    loadDynamicPagesConfigSuccess,
    (state, action): State => {
      return {
        ...state,
        dynamicPagesConfig: action.config,
        isDynamicPagesConfigLoaded: true,
      }
    }
  )
)

export const appReducer = (state: State, action: Action): State =>
  reducer(state, action);

const getUniqueErrorsByCode = (errors: ApiError[]) => {
  const seenCodes = new Set();
  return errors.filter(error => {
    if (seenCodes.has(error.code)) {
      return false;
    } else {
      seenCodes.add(error.code);
      return true;
    }
  });
};

const setSkipToPaymentPath = (state: State): PathStep[] => {
  return state.pathSteps.map((s) => {
      return [FlowPathEnum.TRAVELERS, FlowPathEnum.BAGS, FlowPathEnum.SEATS, FlowPathEnum.MEALS, FlowPathEnum.EXTRAS].includes(s.name)
        ? {...s, completed: true, disabled: false}
        : s;
    })
}
